import { utils, query } from '@kurtosys/ksys-app-template';
import { computed, action, isArrayLike } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IBarChartConfiguration } from '../models';
import { IBarChartProps } from '@kurtosys/ksys-app-components/dist/components/charting/BarChart/models/IBarChartProps';
import { IDisclaimerProps } from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimer/models/IDisclaimerProps';
import { getChartDataFromTable } from '@kurtosys/ksys-app-components/dist/components/charting/ChartBase/ChartBaseFunctions';
import { ITableColumn } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableColumn';
import { IGlobalConfiguration } from '@kurtosys/ksys-app-components/dist/models/IGlobalConfiguration';
import { IQueryOptionsSort } from '../../../models/commonTypes';
import { IBarProps } from '@kurtosys/ksys-app-components/dist/models/charting';

export class BarChartStore extends StoreBase {
	static componentKey: 'barChart' = 'barChart';

	@computed
	get configuration(): IBarChartConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(BarChartStore.componentKey);
		}
	}

	@computed
	get globalConfiguration(): IGlobalConfiguration | undefined {
		return this.storeContext.appStore && this.storeContext.appStore.globalConfiguration;
	}

	@action
	async initialize(): Promise<void> {

	}

	@computed
	get disclaimers(): IDisclaimerProps[] {
		const { queryStore } = this.storeContext;
		const disclaimers = this.configuration && this.configuration.disclaimers;
		if (disclaimers) {
			return disclaimers.map((disclaimer) => {
				return query.Query.convertQueryKeyValue({ text: disclaimer }, queryStore.executionOptions) as IDisclaimerProps;
			});
		}
		return [];
	}

	@computed
	get rows(): object[] {
		let rowsQueries = this.configuration && this.configuration.data;
		if (rowsQueries) {
			if (!isArrayLike(rowsQueries)) {
				rowsQueries = [rowsQueries];
			}
			let rows: object[] = [];
			for (const rowsQuery of rowsQueries) {
				rows = rows.concat(this.getQueryValue(rowsQuery));
			}
			return rows;
		}
		return [];
	}

	@computed
	get columns(): ITableColumn[] {
		let mergedDataPoints: ITableColumn[] | undefined;

		if (this.configuration) {
			const barChartPropsAndQueries = this.mergeQueriesAndProps({
				props: { dataPoints: this.configuration.dataPoints },
				queries: { dataPoints: this.configuration.dataPointsQuery },
			}) as IBarChartConfiguration;

			mergedDataPoints = barChartPropsAndQueries.dataPoints;
		}

		if (isArrayLike(mergedDataPoints)) {
			return mergedDataPoints;
		}

		return [];
	}

	@computed
	get labelListProps(): any {
		const chartProps = this.configuration && this.configuration.chartProps;
		return chartProps &&
			chartProps.commonSeriesProps &&
			chartProps.commonSeriesProps.labelList;
	}

	@computed
	get dataPointNames(): any[] | undefined {
		let names;
		if (this.configuration) {
			const { dataPointNames } = this.configuration;
			if (dataPointNames) {
				names = dataPointNames.map(name => this.getQueryValue(name));
			}
		}
		return names;
	}

	@computed
	get dynamicSeries(): IBarProps[] {
		const seriesQuery = this.configuration && this.configuration.seriesQuery;
		const dynamicSeries: IBarProps[] = seriesQuery ? this.getQueryValue(seriesQuery) : undefined;
		return dynamicSeries;
	}

	@computed
	get debugLogs(): boolean {
		return !!(this.configuration && this.configuration.debugLogs);
	}

	getSeriesProps(labelListProps: any = null): any[] {
		if (this.configuration && this.configuration.seriesQuery) {
			return this.dynamicSeries;
		}


		// Use table column method for defining series properties
		if (this.configuration && (this.configuration.dataPoints || this.configuration.dataPointsQuery)) {
			const columns = this.columns;
			const nameColumn = columns && columns[0];
			const rowNameKey = (nameColumn && nameColumn.key) || 'name';
			return this.rows.map((row: any, index: number) => {
				const seriesName = (this.dataPointNames && this.dataPointNames[index]) || (nameColumn && nameColumn.header) || (row && row[rowNameKey]);
				return {
					dataKey: `series_${ index }`,
					name: seriesName,
					labelList: labelListProps || this.labelListProps,
					fill: row.color,
				};
			});
		}

		// Otherwise, use the series properties passed
		const chartProps = this.configuration && this.configuration.chartProps;
		const seriesProps = chartProps && chartProps.seriesProps;
		if (chartProps && seriesProps) {
			// Apply series prop name overrides
			const seriesPropsOverrides = this.configuration && this.configuration.seriesPropsOverrides;
			if (isArrayLike(seriesPropsOverrides) && isArrayLike(seriesProps)) {
				const seriesPropsByDataKey = utils.collection.pivotCollection(seriesProps, seriesProp => !utils.typeChecks.isNullOrUndefined(seriesProp.dataKey) ? seriesProp.dataKey.toString() : '');
				seriesPropsOverrides.forEach((seriesPropOverride) => {
					const seriesProp = seriesPropsByDataKey[seriesPropOverride.dataKey];
					if (seriesProp) {
						const name = this.getQueryValue(seriesPropOverride.name);
						seriesProp.name = name;
					}
				});
			}
			return seriesProps;
		}

		return [];
	}

	@computed
	get data(): any[] {
		let data;
		const { appStore } = this.storeContext;

		// define sort config
		let sortConfig: IQueryOptionsSort | undefined = this.configuration && this.configuration.dataSort;
		if (!sortConfig) {
			// if there is no sort config try pull the sort from the data source config
			const dataSource = this.configuration && this.configuration.data;
			if (dataSource) {
				if (isArrayLike(dataSource)) {
					const dataSourceWithSort = dataSource.find(config => config.sort);
					if (dataSourceWithSort) {
						sortConfig = dataSourceWithSort.sort as IQueryOptionsSort;
					}
				}
				else if (dataSource.sort) {
					sortConfig = dataSource.sort as IQueryOptionsSort;
				}
			}
		}

		// extract data
		if (this.configuration && (this.configuration.dataPoints || this.configuration.dataPointsQuery)) {
			const tableProps = { rows: this.rows, columns: this.columns };
			data = getChartDataFromTable(tableProps, this.globalConfiguration, this.storeContext.translationStore.translationHelper);
		}
		else {
			data = this.rows;
		}
		// remove empty data points
		if (appStore) {
			const appConfiguration = appStore.getComponentConfiguration('app');
			if (appConfiguration) {
				const { noDataOptions } = appConfiguration;
				if (noDataOptions && noDataOptions.hideDataPoints) {
					const seriesProps = this.getSeriesProps();
					if (seriesProps) {
						data = data.filter((record: any) => {
							return seriesProps.some(prop => prop.dataKey && record[prop.dataKey] !== undefined);
						});
					}
				}
			}
		}

		// Apply the average line data points
		if (this.averageLineConfig) {
			// Apply the start and end data points for the average line, it does not matter where in the data set they are
			const { axisDataKey, averageValueQuery, ...averageLineConfig } = this.averageLineConfig;
			const axisData = data.map((dataPoint: any) => dataPoint[axisDataKey]).sort();
			const axisDataType = typeof axisData[0];
			if (axisDataType === 'number' || Number(axisData[0]) !== NaN) {
				if (averageLineConfig.props) {
					const averageValue = this.getQueryValue(averageValueQuery);
					const minMax = [Number(axisData[0]) - 1, Number(axisData[axisData.length - 1]) + 1];
					for (const value of minMax) {
						// We will need to create a new unique key in the data set to ensure that there is just two data points for the line
						const dataPoint: any = {
							_averageLineData: averageValue,
						};
						dataPoint[axisDataKey] = axisDataType === 'string' ? value.toString() : value;
						data.push(dataPoint);
					}
				}
				else {
					console.warn(`No props provided to the average line, can't draw an average line`);
				}
			}
			else {
				console.warn(`Axis Data is not numeric, can't draw an average line`);
			}
		}

		// apply sort
		if (sortConfig) {
			const ValueQuery = query.ValueQuery;
			const valueQuery = new ValueQuery({ queryOptionsType: 'none', value: [] });
			data = valueQuery.applySort(data, sortConfig);
		}
		return data;
	}

	@computed
	get averageLineConfig() {
		return this.configuration && this.configuration.averageLine;
	}

	@computed
	get colors() {
		return (this.chartProps && this.chartProps.colors) || (this.data || []).map(row => row.color);
	}

	@computed
	get hasData() {
		// if configuration specifies seriesProps, then we will search for those in the data.
		const seriesProps = this.getSeriesProps();
		if (seriesProps) {
			let seriesFound = false;
			for (let i = 0; i < seriesProps.length; i++) {
				const seriesItem = seriesProps[i];
				const dataKey = seriesItem.dataKey;
				if (dataKey !== undefined) {
					const dataItem = this.data.find(item => item[dataKey.toString()] !== undefined);
					if (dataItem !== undefined) {
						seriesFound = true;
						break;
					}
				}
			}
			return seriesFound;
		}
		// if configuration does not specify seriesProps, then we will use the auto-generated seriesProps to search.
		return !utils.typeChecks.isNullOrEmpty(this.data) && this.data.reduce((hasValues, point) => {
			const pointProps = Object.getOwnPropertyNames(point);
			pointProps.forEach((prop) => {
				if (prop.indexOf('series_') === 0 && point[prop] !== undefined) {
					hasValues = true;
				}
			});
			return hasValues;
		}, false);
	}

	@computed
	get noDataPlaceholder() {
		const appStore = this.storeContext.appStore;
		if (appStore.appComponentConfiguration && appStore.appComponentConfiguration.noDataOptions) {
			const { noDataPlaceholder } = appStore.appComponentConfiguration.noDataOptions;
			return noDataPlaceholder;
		}
		return;
	}

	@computed
	get chartProps() {
		const chartProps = this.configuration && this.configuration.chartProps;
		const breakpoints = chartProps && chartProps.breakpoints;
		if (!utils.typeChecks.isNullOrEmpty(breakpoints)) {
			breakpoints.forEach((breakpoint: any) => {
				const { props } = breakpoint;
				if (props) {
					const labelList = props.commonSeriesProps && props.commonSeriesProps.labelList;
					if (labelList) {
						props.seriesProps = this.getSeriesProps(labelList);
					}
					this.setAnimationActive(props.commonSeriesProps);
					this.setAverageLineConfig(props);
					this.resolveAdditionalElementQueries(props);
				}
			});
		}
		const commonSeriesProps = chartProps && chartProps.commonSeriesProps as IBarProps;
		this.setAnimationActive(commonSeriesProps);
		this.setAverageLineConfig(chartProps);
		this.resolveAdditionalElementQueries(chartProps);
		return chartProps;
	}

	resolveAdditionalElementQueries(props?: IBarChartProps) {
		if (props && props.additionalElements) {
			for (const element of props.additionalElements) {
				if (element.queries) {
					const { props, queries } = element;
					element.props = this.mergeQueriesAndProps({ props, queries });
					delete element.queries;
				}
			}
		}
	}

	setAverageLineConfig(props?: IBarChartProps) {
		if (props && this.averageLineConfig) {
			// This is to check that the average line has not been added yet, we dont what to add it each time the props rerender
			const hasAverageLine = props.additionalElements && props.additionalElements.filter((prop: any) => {
				const { props } = prop;
				if (props && props.dataKey) {
					return props.dataKey === '_averageLineData';
				}
				return false;
			});
			// apply the average line config to the additional elements, be sure to update the dataKey to match our unqiue key
			const { axisDataKey, averageValueQuery, ...averageLineConfig } = this.averageLineConfig;
			if (averageLineConfig.props && hasAverageLine && hasAverageLine.length === 0) {
				// The animation interfers with the stroke dash array
				// if not configured then set to false
				if (averageLineConfig.props.isAnimationActive === undefined) {
					averageLineConfig.props.isAnimationActive = false;
				}
				averageLineConfig.props.dataKey = '_averageLineData';
				if (props.additionalElements) {
					props.additionalElements.push(averageLineConfig);
				}
				else {
					props.additionalElements = [averageLineConfig];
				}
			}
		}
	}

	setAnimationActive(commonSeriesProps: IBarProps | undefined) {
		const { appStore } = this.storeContext;
		const { isSnapshot } = appStore;
		if (isSnapshot) {
			if (!commonSeriesProps) {
				commonSeriesProps = { dataKey: 'animation' };
			}
			commonSeriesProps.isAnimationActive = false;
		}
	}

	@computed
	get barChartProps(): IBarChartProps | undefined {
		const chartProps = this.chartProps;
		if (chartProps) {
			return {
				...chartProps,
				colors: this.colors,
				data: this.data,
				seriesProps: this.getSeriesProps(),
				disclaimersProps: {
					...chartProps.disclaimersProps,
					disclaimers: this.disclaimers,
				},
			};
		}
	}
}
